import {
  getUpdatedHotelPriceBreakdownPrices,
  getHotelBreakdownItems,
} from "@apac/checkout";
import {
  type GuestCounts,
  useHotelDateRange,
  useHotelGuestCount,
} from "@apac/hooks";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import type { FiatPrice, PlatformComponentProps } from "@b2bportal/core-types";
import { useCheckoutStateSelector } from "@hopper-b2b/checkout";
import { useI18nContext, useI18nList } from "@hopper-b2b/i18n";
import { Icon, Popover, PopoverContent, PopoverTrigger } from "@tenant/ui";
import clsx from "clsx";
import { useApacHotelsStyles } from "../../hooks";
import { ApacHotelsComponent } from "../../types";
import { FintechPriceBreakdownSection } from "../FintechPriceBreakdownSection";
import { PayLaterBreakdownSection } from "../PayLaterBreakdownSection";
import defaultStyles from "./styles.module.scss";

export interface BasePriceBreakdownProps extends PlatformComponentProps {
  separator?: string;
  validateRoomPrice?: boolean;
  showBedAndGuests?: boolean;
  showRoomInfo?: boolean;
  // this showing breakdown user currency
  showPayLaterBreakdown?: boolean;
  currencyDifference?: FiatPrice;
  guestCounts?: GuestCounts;
  fiatFormatOptions?: Intl.NumberFormatOptions;
}

export const BasePriceBreakdown = ({
  className,
  separator = "x",
  validateRoomPrice = false,
  showPayLaterBreakdown = false,
  showBedAndGuests = false,
  showRoomInfo = false,
  currencyDifference,
  guestCounts,
  fiatFormatOptions,
}: BasePriceBreakdownProps) => {
  const COMPONENT_KEY = ApacHotelsComponent.BasePriceBreakdown;
  const styles = useApacHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { t, formatFiatCurrency } = useI18nContext();
  const { formatList } = useI18nList();
  const { dateFormattedString } = useHotelDateRange({ useShopData: true });

  const {
    payLaterBreakdown,
    roomPrice,
    taxesAndFees,
    stayNights,
    bedTypes,
    roomName,
    roomsCount,
  } = useCheckoutStateSelector(
    validateRoomPrice
      ? getUpdatedHotelPriceBreakdownPrices
      : getHotelBreakdownItems
  );

  const { formattedGuestCountString } = useHotelGuestCount(guestCounts);
  const totalNightsTranslated = t("nights", {
    count: stayNights,
  });
  const roomsString = t("recentSearch.hotels.rooms", {
    count: roomsCount ?? 1,
  });
  const infoStrings = [totalNightsTranslated];
  infoStrings.push(roomsString);
  if (showBedAndGuests) {
    infoStrings.push(formattedGuestCountString());
  }

  const bedLabel = formatList(
    bedTypes?.rooms?.map((room) => room.bedTypes.description) ?? [],
    { type: "conjunction" }
  );

  return (
    <div className={clsx(block, className)}>
      <div className={cn("line-container")}>
        <div className={cn("base-price-label")}>
          <span className={cn("passengers-info")}>
            {infoStrings.join(separator)}
          </span>

          {showRoomInfo && <span className={cn("room-info")}>{roomName}</span>}
          {showBedAndGuests && (
            <span className={cn("bed-info")}>{bedLabel}</span>
          )}
          <span className={cn("date-info")}>({dateFormattedString})</span>
        </div>

        <div className={cn("summary-item-value")}>
          {formatFiatCurrency(roomPrice?.fiat, fiatFormatOptions)}
        </div>
      </div>

      <div className={cn("line-container")}>
        <Popover>
          <PopoverTrigger>
            <div className={cn("summary-item-label")}>
              {t("apac.taxesAndServiceCharges")}
              <Icon
                className="taxes-popover-fees-icon"
                iconName={IconNameEnum.ui__infoPopover}
              />
            </div>
          </PopoverTrigger>
          <PopoverContent
            className={cn("taxes-popover-content")}
            showCloseButton={false}
          >
            {t("core-checkout.priceBreakdown.taxesAndFeesTooltip")}
          </PopoverContent>
        </Popover>
        <div className={cn("summary-item-value")}>
          {formatFiatCurrency(taxesAndFees?.fiat, fiatFormatOptions)}
        </div>
      </div>

      <FintechPriceBreakdownSection />

      {currencyDifference != null && (
        <div className={cn("line-container")}>
          <Popover>
            <PopoverTrigger>
              <div className={cn("summary-item-label")}>
                {t("apac.checkout.review.currencyConversion")}
                <Icon
                  className="taxes-popover-fees-icon"
                  iconName={IconNameEnum.ui__infoPopover}
                />
              </div>
            </PopoverTrigger>
            <PopoverContent
              className={cn("taxes-popover-content")}
              showCloseButton={false}
            >
              {t(
                "apac.checkout.review.currencyConversionDisclaimerDescription"
              )}
            </PopoverContent>
          </Popover>
          <div className={cn("summary-item-value")}>
            {formatFiatCurrency(currencyDifference, fiatFormatOptions)}
          </div>
        </div>
      )}

      {showPayLaterBreakdown && payLaterBreakdown != null && (
        <PayLaterBreakdownSection feeBreakdown={payLaterBreakdown} />
      )}
    </div>
  );
};
